import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Trans, useTranslation} from 'react-i18next';
import Select from '../../../../../../../select';
import Input from '../../../../../../../input';
import Button from '../../../../../../../button';
import {
	addCriteria, removeCriteria,
	updateCriteria
} from "../../../../../../../../../actions/advanceSearchActions";
import {
	formatDateForInput, normalizeCriteriaValue,
} from "../../../../../../../../utils";

function Criteria ({ index, id, field, operator, value }) {

	const dispatch = useDispatch();

	const allCriteria = useSelector(state => state.advanceSearchReducer.advanceSearchCriteria);
	const customFieldsForContactForm = useSelector(state => state.customFieldsReducer.customFieldsForContactForm);

	const { t } = useTranslation();

	const [error, setError] = useState(null);

	const isOperatorWithoutValue = operator === 'IS_EMPTY' || operator === 'IS_NOT_EMPTY' || operator === '';

	const handleFieldChange = (e) => {
		const newField = e.target.value;
		setError(null);

		const isDuplicate = allCriteria.some((crit, i) =>
			i !== index &&
			crit.field === newField &&
			crit.operator === operator
		);

		if (isDuplicate) {
			dispatch(updateCriteria(index, { field: newField, operator: '', value: '', error: null }));
		} else {
			dispatch(updateCriteria(index, { field: newField, value: '', error: null }));
		}
	};


	const handleOperatorChange = (e) => {
		const newOperator = e.target.value;

		if (newOperator === 'IS_EMPTY' || newOperator === 'IS_NOT_EMPTY') {
			setError(null);
			dispatch(updateCriteria(index, { operator: newOperator, value: '', error: null }));
		} else {
			dispatch(updateCriteria(index, { operator: newOperator, error: null }));
		}
	};

	const handleValueChange = (e) => {
		const rawValue = e.target.value;
		const { value: normalizedValue, error } = normalizeCriteriaValue(field, rawValue);

		setError(error);
		dispatch(updateCriteria(index, { value: normalizedValue, error }));
	};

	const handleAdd = () => {
		dispatch(addCriteria());
	};

	const handleRemove = () => {
		dispatch(removeCriteria(index));
	};

	const allOperators = [
		'IS',
		'IS_EMPTY',
		'IS_NOT_EMPTY',
		'CONTAINS',
		'NOT_CONTAINS',
		'GREATER_THAN',
		'LOWER_THAN',
		'IS_BEFORE',
		'IS_AFTER'
	];

	const dateFields = ['birthday', 'createdDate', 'modifiedTime'];

	let filteredOperators = allOperators;
	if (dateFields.includes(field)) {
		filteredOperators = ['IS_BEFORE', 'IS_AFTER'];
	}

	const usedOperatorsForField = allCriteria
		.filter((crit, i) => i !== index && crit.field === field)
		.map(crit => crit.operator);

	const availableOperators = filteredOperators.filter(op => (
		!usedOperatorsForField.includes(op) || op === operator
	));

	const getPlaceholder = () => {
		if (!operator) return t('advancedSearchPlaceHolderSelectAnOperator');
		if (operator === "IS_EMPTY" || operator === "IS_NOT_EMPTY") return t('advancedSearchPlaceHolderNoValueRequired');
		if (dateFields.includes(field)) return t('advancedSearchPlaceHolderSelectADate');
		return t('advancedSearchPlaceHolderEnterValue');
	};

	const getAriaLabel = () => {
		if (!operator) return t('advancedSearchSelectAriaValue');
		if (operator === "IS_EMPTY" || operator === "IS_NOT_EMPTY") return t('advancedSearchSelectAriaValueDisabled');
		if (dateFields.includes(field)) return <Trans i18nKey="advancedSearchSelectAriaDateValue" values={{field: field}}/>;
		return <Trans i18nKey="advancedSearchSelectAriaValueForFieldWithOperator" values={{field: field, operator: operator}}/>;
	};

	return (
		<div
			id={id}
			role="group"
			aria-labelledby={`criteria-label-${index}`}
			className="criteria d-flex align-items-center mb-4"
		>
			<span id={`criteria-label-${index}`} className="sr-only">
				Criteria {index + 1}
			</span>

			<div className="col-4">
				<label htmlFor={`asField${index}`} className="sr-only">Field</label>
				<Select
					id={`asField${index}`}
					value={field}
					onChange={handleFieldChange}
					aria-label="Select field"
				>
					<option value="">-- {t('advancedSearchSelectField')} --</option>
					<option value="name">Name</option>
					<option value="department">Department</option>
					<option value="phoneNumber">Phone Number</option>
					<option value="email">Email</option>
					<option value="postalAddress">Postal Address</option>
					<option value="state">State</option>
					<option value="country">Country</option>
					<option value="company">Company</option>
					<option value="job">Job</option>
					<option value="birthday">Birthday</option>
					<option value="createdDate">Created Date</option>
					<option value="modifiedTime">Modified Time</option>
					{
						customFieldsForContactForm.customFields && customFieldsForContactForm.customFields.length > 0 && customFieldsForContactForm.customFields.map( (customField) => (
							<option key={customField.id} value={customField.label}>{customField.label}</option>
						))
					}
				</Select>
			</div>
			<div className="col-3">
				<label htmlFor={`asOperator${index}`} className="sr-only">{t('advancedSearchSelectOperator')}</label>
				<Select
					id={`asOperator${index}`}
					value={operator}
					onChange={handleOperatorChange}
					aria-label="Select operator"
				>
					<option value="">-- {t('advancedSearchSelectOperator')} --</option>
					{availableOperators.map(op => (
						<option key={op} value={op}>{op}</option>
					))}
				</Select>
			</div>
			<div className="col-4">
				<label htmlFor={`asValue${index}`} className="sr-only">{t('advancedSearchSelectValue')}</label>
				{dateFields.includes(field) ? (
					<Input
						type="date"
						id={`asValue${index}`}
						value={formatDateForInput(value)}
						onChange={handleValueChange}
						disabled={isOperatorWithoutValue}
						placeholder={getPlaceholder()}
						aria-label={getAriaLabel()}
						className={error ? "is-invalid" : ""}
					/>
				) : (
					<Input
						type="text"
						id={`asValue${index}`}
						value={value}
						onChange={handleValueChange}
						disabled={isOperatorWithoutValue}
						placeholder={getPlaceholder()}
						aria-label={getAriaLabel()}
						className={error ? "is-invalid" : ""}
					/>
				)}
				{error && <div className="criterion__error text-danger small mt-1">{error}</div>}
			</div>
			<div className="col-1">
				{
					index === 0 ?
						<Button
							type="button"
							className="btn-link p-0"
							onClick={handleAdd}
							aria-label="Add a new filter"
						>
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none"
								 xmlns="http://www.w3.org/2000/svg">
								<path
									d="M9.445 15h1.111v-4.445H15v-1.11h-4.445V5h-1.11v4.445H5v1.111h4.445zm.559 5a9.7 9.7 0 0 1-3.9-.787 10.1 10.1 0 0 1-3.177-2.136 10.1 10.1 0 0 1-2.138-3.175A9.7 9.7 0 0 1 0 10.004q0-2.074.787-3.9a10.1 10.1 0 0 1 2.136-3.177A10.1 10.1 0 0 1 6.098.788 9.7 9.7 0 0 1 9.996 0q2.074 0 3.9.787a10.1 10.1 0 0 1 3.177 2.136 10.1 10.1 0 0 1 2.138 3.175Q20 7.922 20 9.996a9.7 9.7 0 0 1-.787 3.9 10.1 10.1 0 0 1-2.136 3.177q-1.35 1.35-3.175 2.138a9.7 9.7 0 0 1-3.898.788M10 18.889q3.723 0 6.305-2.584T18.889 10t-2.584-6.305Q13.723 1.111 10 1.111T3.695 3.695 1.111 10t2.584 6.305Q6.277 18.889 10 18.889"
									fill="currentColor"/>
							</svg>
						</Button>
						:
						<Button
							data-index={index}
							className="btn-link p-0"
							onClick={handleRemove}
							aria-label={`Remove filter ${index + 1}`}
						>
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none"
								 xmlns="http://www.w3.org/2000/svg">
								<path
									d="M15 10.555V9.446H5v1.109zM10 20a9.7 9.7 0 0 1-3.9-.791 10.1 10.1 0 0 1-3.173-2.136A10.1 10.1 0 0 1 .791 13.9 9.7 9.7 0 0 1 0 10q0-2.073.791-3.9a10.1 10.1 0 0 1 2.136-3.173A10.1 10.1 0 0 1 6.1.791 9.7 9.7 0 0 1 10 0q2.073 0 3.9.791a10.1 10.1 0 0 1 3.173 2.136A10.1 10.1 0 0 1 19.209 6.1Q20 7.918 20 10a9.7 9.7 0 0 1-.791 3.9 10.1 10.1 0 0 1-2.136 3.173q-1.345 1.355-3.173 2.136A9.7 9.7 0 0 1 10 20m0-1.109q3.718 0 6.309-2.582T18.891 10t-2.582-6.309Q13.727 1.109 10 1.109T3.691 3.691 1.109 10t2.582 6.309Q6.282 18.891 10 18.891"
									fill="currentColor"/>
							</svg>
						</Button>
				}
			</div>
		</div>
	)
}

export default Criteria;
